import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { AdminGuard } from "@/components/layout/AdminGuard";
import { AuthGuard } from "@/components/layout/AuthGuard";
import { AuthLayout } from "@/components/layout/AuthLayout";
import { PublicOnlyGuard } from "@/components/layout/PublicOnlyGuard";
import { Icon } from "@/components/ui/Icon";
import { ADMIN_ROUTE_SEGMENTS, ROUTES } from "@/constants/routes";
import { LoginPage } from "@/pages/auth/LoginPage";
import { ReferralCapturePage } from "@/pages/auth/ReferralCapturePage";
import { RegisterPage } from "@/pages/auth/RegisterPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { FuelPurchasePage } from "@/pages/FuelPurchasePage";
import { HelpPage } from "@/pages/HelpPage";
import { LandingPage } from "@/pages/LandingPage";
import { ProfilePage } from "@/pages/ProfilePage";
import { ProtectPage } from "@/pages/ProtectPage";
import { ShortHelpPage } from "@/pages/ShortHelpPage";
import { TopUpsPage } from "@/pages/TopUpsPage";

const AnalyticsPage = lazy(() =>
  import("@/pages/AnalyticsPage").then((module) => ({ default: module.AnalyticsPage }))
);
const AdminBotsPage = lazy(() =>
  import("@/pages/admin/AdminBotsPage").then((module) => ({ default: module.AdminBotsPage }))
);
const AdminBotConfigsPage = lazy(() =>
  import("@/pages/admin/AdminBotConfigsPage").then((module) => ({
    default: module.AdminBotConfigsPage,
  }))
);
const AdminCoinSetsPage = lazy(() =>
  import("@/pages/admin/AdminCoinSetsPage").then((module) => ({
    default: module.AdminCoinSetsPage,
  }))
);
const AdminProductAnalyticsPage = lazy(() =>
  import("@/pages/admin/AdminProductAnalyticsPage").then((module) => ({
    default: module.AdminProductAnalyticsPage,
  }))
);
const AdminUsersPage = lazy(() =>
  import("@/pages/admin/AdminUsersPage").then((module) => ({ default: module.AdminUsersPage }))
);
const AdminUserDealsPage = lazy(() =>
  import("@/pages/admin/AdminUserDealsPage").then((module) => ({
    default: module.AdminUserDealsPage,
  }))
);
const AdminUserBotsPage = lazy(() =>
  import("@/pages/admin/AdminUserBotsPage").then((module) => ({
    default: module.AdminUserBotsPage,
  }))
);
const AdminWorkersPage = lazy(() =>
  import("@/pages/admin/AdminWorkersPage").then((module) => ({
    default: module.AdminWorkersPage,
  }))
);

export const AppRoutes = () => (
  <Routes>
    {/* Landing page */}
    <Route element={<PublicOnlyGuard />}>
      <Route
        index
        element={<LandingPage />}
      />
    </Route>

    {/* Auth routes */}
    <Route
      path={ROUTES.referral}
      element={<ReferralCapturePage />}
    />
    <Route element={<AuthLayout />}>
      <Route
        path={ROUTES.register}
        element={<RegisterPage />}
      />
      <Route
        path={ROUTES.login}
        element={<LoginPage />}
      />
    </Route>

    {/* Help Page*/}
    <Route
      path={ROUTES.help}
      element={<HelpPage />}
    />
    <Route
      path={ROUTES.shortHelp}
      element={<ShortHelpPage />}
    />
    <Route
      path={ROUTES.protect}
      element={<ProtectPage />}
    />

    <Route element={<AuthGuard />}>
      {/* Profile page (standalone layout) */}
      <Route
        path={ROUTES.profile}
        element={<ProfilePage />}
      />

      {/* Dashboard page */}
      <Route
        path={ROUTES.dashboard}
        element={<DashboardPage />}
      />

      {/* Analytics page */}
      <Route
        path={ROUTES.analytics}
        element={
          <Suspense
            fallback={
              <div className="bg-background flex min-h-screen w-full items-center justify-center">
                <Icon
                  name="spinner"
                  className="size-6 animate-spin text-neutral-500"
                />
              </div>
            }
          >
            <AnalyticsPage />
          </Suspense>
        }
      />

      {/* Fuel page */}
      <Route
        path={ROUTES.buyFuel}
        element={<FuelPurchasePage />}
      />
      <Route
        path={ROUTES.topUps}
        element={<TopUpsPage />}
      />
      <Route
        path={ROUTES.buyFuelPayment}
        element={
          <Navigate
            to={ROUTES.buyFuel}
            replace
          />
        }
      />

      {/* Admin routes (protected, admin only) */}
      <Route
        path={ROUTES.admin.root}
        element={<AdminGuard />}
      >
        <Route
          index
          element={
            <Navigate
              to={ROUTES.admin.bots}
              replace
            />
          }
        />
        <Route
          path={ADMIN_ROUTE_SEGMENTS.bots}
          element={<AdminBotsPage />}
        />
        <Route
          path={ADMIN_ROUTE_SEGMENTS.botConfigs}
          element={<AdminBotConfigsPage />}
        />
        <Route
          path={ADMIN_ROUTE_SEGMENTS.coinSets}
          element={<AdminCoinSetsPage />}
        />
        <Route
          path={ADMIN_ROUTE_SEGMENTS.workers}
          element={<AdminWorkersPage />}
        />
        <Route
          path={ADMIN_ROUTE_SEGMENTS.productAnalytics}
          element={<AdminProductAnalyticsPage />}
        />
        <Route
          path={ADMIN_ROUTE_SEGMENTS.users}
          element={<AdminUsersPage />}
        />
        <Route
          path={ADMIN_ROUTE_SEGMENTS.userDeals}
          element={<AdminUserDealsPage />}
        />
        <Route
          path={ADMIN_ROUTE_SEGMENTS.userBots}
          element={<AdminUserBotsPage />}
        />
      </Route>
    </Route>
  </Routes>
);
